/**
 * The studio-services side of the site.
 *
 * Naming follows what the tabletop industry actually calls these things, which
 * is not always what it sounds like from outside:
 *
 * - **Design** is originating the game — concept, mechanics, the first rules.
 * - **Development** is a separate, paid discipline: taking a design that
 *   already exists and making it balanced, teachable and manufacturable. A
 *   publisher who "signs a design" then "develops" it. Selling the two under
 *   one word loses the half most clients are actually shopping for.
 * - **Blind playtesting** specifically means handing over the rulebook and the
 *   components and leaving — no designer in the room to answer questions. It is
 *   named separately from playtesting because it tests the rulebook, not the
 *   game.
 * - **Digital adaptation** (or digital implementation / digital edition) is the
 *   physical-to-online port. This is the one Neon Crow has the least competition
 *   on and the most evidence for.
 *
 * Nothing here quotes a price. Scope varies enough that every one of these ends
 * in a conversation, and a wrong number on a public page is worse than none.
 */

export interface Service {
  slug: string;
  name: string;
  /** The industry's other names for it, so searchers land on the right page. */
  alsoCalled?: string;
  summary: string;
  /** What the client actually receives. */
  deliverables: string[];
  /** Why Neon Crow specifically. Only claims the repo can back up. */
  evidence?: string;
}

export const SERVICES: Service[] = [
  {
    slug: "digital-adaptation",
    name: "Digital adaptation",
    alsoCalled: "Digital implementation · digital edition",
    summary:
      "You have a physical card or board game. We make it a real one you can play " +
      "online — rules enforced by an engine, not by the honour system. Matchmaking, " +
      "private tables, spectators, and a table view that reads on a stream.",
    deliverables: [
      "A playable browser build of your game, rules enforced server-side",
      "Online multiplayer for the player counts your game supports, plus AI opponents for solo play and testing",
      "Card data imported from what you already have — a Tabletop Simulator export, a spreadsheet, or the printed cards themselves",
      "A spectator view and an OBS overlay, so the game can be streamed and watched",
      "A 3D table for the group that wants to pick the cards up",
    ],
    evidence:
      "This is the platform Neon Crow already runs. Five games sit on it, every one " +
      "built from nothing more than a rulebook and a Tabletop Simulator export. Three " +
      "of them took the engine exactly as it was; the fourth wanted dice, armour that " +
      "comes off a card one plate at a time, and a heat gauge, and got them without " +
      "disturbing the others — and the fifth arrived and shipped the same day.",
  },
  {
    slug: "development",
    name: "Game development",
    alsoCalled: "Development pass · design refinement",
    summary:
      "The discipline a publisher applies after signing a design. The game works; " +
      "now it has to be balanced, teachable, and buildable. We play it, break it, " +
      "and tell you exactly where it bends.",
    deliverables: [
      "A written development report: what works, what is degenerate, what to cut",
      "Balance passes on card values, costs, turn counts and game length",
      "Edge cases and rules interactions written out, with the ruling for each",
      "Component counts and deck compositions checked against how the game actually plays",
    ],
    evidence:
      "A line-by-line audit of Cryptid Files against its rulebook turned up four " +
      "places the printed board contradicted the book, and a tackle set two fish " +
      "shared that the rules said was unique. That is what this looks like.",
  },
  {
    slug: "rules-audit",
    name: "Rules audit & rulebook editing",
    alsoCalled: "Rules review · developmental edit · proofread",
    summary:
      "Two jobs that get sold as one. The audit asks whether the rulebook describes " +
      "the game you actually built. The edit asks whether a stranger can learn it " +
      "from the page without asking you a question.",
    deliverables: [
      "An audit reconciling the rulebook against the game as played, every disagreement listed",
      "A structural edit — order of teaching, what belongs in a reference sheet, what belongs in an appendix",
      "A terminology pass: one name per concept, capitalised the same way every time",
      "A final proofread once the rulebook is laid out",
    ],
  },
  {
    slug: "playtesting",
    name: "Playtesting & blind playtesting",
    alsoCalled: "Structured playtest · blind test",
    summary:
      "Run tests, not vibes. Facilitated sessions where we know the game and probe it " +
      "deliberately — and blind sessions where a group gets the box and the rulebook " +
      "and nothing else, which is the only honest test of whether the rules teach.",
    deliverables: [
      "Facilitated sessions against a stated question — is turn three dead, does the leader run away with it",
      "Blind sessions: rulebook and components handed over, no designer in the room, every question the table asked written down",
      "A report per round, with the changes we would make and the ones we would not",
      "Where it helps, a digital build so a hundred games can be simulated instead of ten played",
    ],
    evidence:
      "Because the games run on an engine, a change can be tested across hundreds of " +
      "simulated games overnight rather than across a month of table nights.",
  },
  {
    slug: "design",
    name: "Game design",
    alsoCalled: "Original design · expansion design · work for hire",
    summary:
      "An original design to a brief — a promotional game, an expansion to something " +
      "you already publish, or a solo mode for a game that never had one.",
    deliverables: [
      "Concept and mechanics to a brief, with the reasoning for each choice written down",
      "A working prototype, physical or digital, that can be put in front of people",
      "Expansions, variants, and solo or cooperative modes for existing titles",
      "A first-draft rulebook",
    ],
  },
];

export const serviceBySlug = (slug: string) => SERVICES.find((s) => s.slug === slug);

/**
 * The two doors onto the services page. Most enquiries are one or the other,
 * and the answer is different enough that it is worth asking up front.
 */
export const ENTRY_POINTS = [
  {
    title: "Have an idea?",
    body:
      "A mechanic on a napkin, a prototype in a shoebox, or a game you have been " +
      "describing to people for two years. Tell us what it is and we will tell you " +
      "honestly what it would take — including when the answer is that it is not " +
      "ready yet.",
    cta: "Tell us about it",
  },
  {
    title: "Already have a game?",
    body:
      "A printed game, a Kickstarter that delivered, or a design you have been " +
      "playing at home for years. We turn it into a digital edition people can " +
      "play online, keep it faithful to your rules, and hand the rules engine back " +
      "to you documented.",
    cta: "Get it online",
  },
];

/**
 * What the studio is, in the words a stranger needs.
 *
 * Its home is the philosophy band on the front page, which is the one part of
 * the site that talks about the people rather than the games. Kept here beside
 * the services copy because both are about the studio, and the two should
 * never drift into telling different stories about it.
 */
export const STUDIO = {
  eyebrow: "Our philosophy",
  title: "We make games we want to play",
  body:
    "Neon Crow Games is an independent tabletop studio making immersive, " +
    "story-driven games for curious people. We believe in great stories, good " +
    "company, and the thrill of not quite knowing what is out there.",
  script: "Strange\nStories\nBrighter\nTogether.",
};
